import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, Fade, CircularProgress } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import Dashboard from './Dashboard';
import SuperAdminDashboard from './SuperAdminDashboard';

// Enhanced console logging setup
const log = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] ${message}`, data || '');
  // Also log to browser's alert for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 DEBUG: ${message}`);
  }
};

const logError = (message, error = null) => {
  const timestamp = new Date().toISOString();
  console.error(`[${timestamp}] ERROR: ${message}`, error || '');
  // Also log to browser's alert for debugging
  if (process.env.NODE_ENV === 'development') {
    console.error(`💥 ERROR: ${message}`, error);
  }
};

// API configuration
let getLoginUrl, getSendEmailOtpUrl;
try {
  log('🔧 Attempting to load API module...');
  const apiModule = require('../config/api');
  getLoginUrl = apiModule.getLoginUrl;
  getSendEmailOtpUrl = apiModule.getSendEmailOtpUrl;
  log('✅ API module loaded successfully');
  log('🔗 getLoginUrl function type:', typeof getLoginUrl);
  log('🔗 getSendEmailOtpUrl function type:', typeof getSendEmailOtpUrl);
} catch (error) {
  logError('❌ Failed to load API module:', error);
  // Fallback functions
  getLoginUrl = () => 'https://api.jpnewz.com/dashboard/login';
  getSendEmailOtpUrl = () => 'https://api.jpnewz.com/dashboard/send_email_forgot_password';
  log('🔄 Using fallback API functions');
}

// Removed static users array - will use API data instead

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [view, setView] = useState('login'); // 'login', 'forgot', 'pin'
  const [email, setEmail] = useState('');
  // const [pin, setPin] = useState('');
  // const [sentPin, setSentPin] = useState('');
  const [forgotError, setForgotError] = useState('');
  // const [pinError, setPinError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Session restoration is now handled in App.js
  // This component only handles login form submission

  // Add click handler for debugging
  const handleButtonClick = (buttonType) => {
    log(`🖱️ Button clicked: ${buttonType}`);
    log('📊 Current state:', {
      username,
      password: password ? '[HIDDEN]' : '[EMPTY]',
      isLoading,
      error,
      view
    });
  };

  // Test console function
  const testConsole = () => {
    log('🧪 Manual console test triggered');
    log('📅 Current time:', new Date().toISOString());
    log('🌐 Window location:', window.location.href);
    log('🔧 User agent:', navigator.userAgent);
    
    // Test different log levels
    console.log('📝 Regular console.log test');
    console.warn('⚠️ Console.warn test');
    console.error('💥 Console.error test');
    console.info('ℹ️ Console.info test');
    
    alert('Console test completed! Check browser console for logs.');
  };

  // Logout is handled by App.js - not needed in Login component

  // Dashboard rendering is now handled by App.js with routing

  log('🔄 Rendering Login component');

  // Validate session with server
  const validateSession = async (sessionId, dashAdminId) => {
    try {
      log('🔍 Validating session with server...');
      log('📤 Sending validation request with sessionId:', sessionId);
      log('📤 Sending validation request with dashAdminId:', dashAdminId);
      
      // Try to validate session with a simple API call
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 1,
          isActiveFilter: true,
          isActive: true,
          isSearchFilter: false,
          search: ""
        }),
      });

      log('📥 Validation response status:', response.status);
      const data = await response.json();
      log('📥 Validation response data:', data);
      
      if (response.ok && data.status === true) {
        log('✅ Session validation successful');
        return true;
      } else {
        log('❌ Session validation failed:', data.message);
        return false;
      }
    } catch (error) {
      logError('💥 Session validation error:', error);
      // If validation fails due to network error, we'll assume session is valid
      // to avoid logging out users unnecessarily
      log('⚠️ Assuming session is valid due to network error');
      return true;
    }
  };

  // Test API login function
  const testApiLogin = async () => {
    log('🧪 Testing API login with hardcoded credentials');
    
    const testCredentials = {
      username: 'jp89OCo9',
      password: 'Jpnews@123'
    };
    
    log('📝 Test credentials:', testCredentials);
    
    try {
      const loginUrl = getLoginUrl();
      log('🌐 Making test API call to:', loginUrl);
      
      log('📤 Starting test fetch request...');
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testCredentials)
      });
      log('📥 Test fetch request completed');

      log('📡 Test response status:', response.status);
      log('📡 Test response headers:', response.headers);

      const data = await response.json();
      log('📄 Test response data:', data);

      if (response.ok && data.status === true) {
        log('✅ Test login successful!');
        alert('✅ API Test: Login successful! Check console for details.');
      } else {
        log('❌ Test login failed:', data.message || 'Unknown error');
        alert(`❌ API Test: Login failed - ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      logError('💥 Test login error:', error);
      alert(`💥 API Test: Network error - ${error.message}`);
    }
  };

  // Test Email OTP API function
  const testEmailOtpApi = async () => {
    log('🧪 Testing Email OTP API with test email');
    
    const testEmail = {
      email: 'newuser@gmail.com'
    };
    
    log('📝 Test email object:', testEmail);
    log('📝 Test email JSON string:', JSON.stringify(testEmail));
    log('📝 Test email validation:', {
      isString: typeof testEmail.email === 'string',
      isEmpty: testEmail.email === '',
      hasAtSymbol: testEmail.email.includes('@'),
      length: testEmail.email.length,
      email: testEmail.email
    });
    
    try {
      const sendOtpUrl = getSendEmailOtpUrl();
      log('🌐 Making test Email OTP API call to:', sendOtpUrl);
      log('🔗 Full test API URL:', sendOtpUrl);
      
      log('📤 Starting test Email OTP fetch request...');
      log('📤 Complete fetch request details:', {
        url: sendOtpUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testEmail),
        bodyString: JSON.stringify(testEmail),
        bodyType: typeof JSON.stringify(testEmail)
      });
      
      const response = await fetch(sendOtpUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testEmail)
      });
      log('📥 Test Email OTP fetch request completed');

      log('📡 Test Email OTP response status:', response.status);
      log('📡 Test Email OTP response headers:', response.headers);

      const data = await response.json();
      log('📄 Test Email OTP response data:', data);

      if (response.ok && data.status === true) {
        log('✅ Test Email OTP successful!');
        alert('✅ Email OTP Test: OTP sent successfully! Check console for details.');
      } else {
        log('❌ Test Email OTP failed:', data.message || 'Unknown error');
        alert(`❌ Email OTP Test: Failed - ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      logError('💥 Test Email OTP error:', error);
      alert(`💥 Email OTP Test: Network error - ${error.message}`);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    log('🚀 Login form submitted!');
    log('📝 Form data:', { 
      username, 
      password: password ? '[HIDDEN]' : '[EMPTY]',
      formValid: username && password 
    });
    
    if (!username || !password) {
      log('❌ Username or password is empty');
      setError('Please enter both username and password.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    log('⏳ Loading state set to true');

    try {
      const loginUrl = getLoginUrl();
      log('🌐 Making API call to:', loginUrl);
      
      const requestBody = {
        username: username,
        password: password
      };
      log('📦 Request body:', requestBody);

      log('📤 Starting fetch request...');
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      log('📥 Fetch request completed');

      log('📡 Response status:', response.status);
      log('📡 Response headers:', response.headers);

      const data = await response.json();
      log('📄 Response data:', data);

      if (response.ok && data.status === true) {
        log('✅ Login successful!');
        log('📄 Full API response data:', data);
        
        // Extract user data from the nested structure
        const userData = data.data || {};
        log('📦 Extracted user data:', userData);
        
        // Store session data in localStorage
        const sessionId = userData.sessionId;
        const dashAdminId = userData.dashAdminId;
        
        // Store in localStorage if found
        if (sessionId) {
          localStorage.setItem('sessionId', sessionId);
          log('💾 sessionId stored in localStorage:', sessionId);
        } else {
          log('⚠️ sessionId not found in API response');
        }
        
        if (dashAdminId) {
          localStorage.setItem('dashAdminId', dashAdminId);
          log('💾 dashAdminId stored in localStorage:', dashAdminId);
        } else {
          log('⚠️ dashAdminId not found in API response');
        }
        
        // Also store the full response for debugging
        localStorage.setItem('loginResponse', JSON.stringify(data));
        log('💾 Full login response stored for debugging');
        
        // Determine user role based on roleType
        let userRole = 'User';
        if (userData.roleType === 1) {
          userRole = 'Super Admin';
        } else if (userData.roleType === 2) {
          userRole = 'Admin';
        } else if (userData.roleType === 3) {
          userRole = 'Reporter';
        } else if (userData.roleType === 4) {
          userRole = 'Approver';
        }
        
        // API login successful - create user object with proper data
        const user = {
          username: userData.username || username,
          name: userData.name || userData.username || username,
          role: userRole,
          email: userData.email || '',
          phone: userData.phone || '',
          roleType: userData.roleType,
          sessionId: sessionId,
          dashAdminId: dashAdminId,
          isActive: userData.isActive,
          createdAt: userData.createdAt,
          ...userData // Include any additional user data from API
        };
        log('👤 User object created:', user);
        
        if (onLogin) {
          onLogin(user);
        }
        setError('');
      } else {
        log('❌ Login failed:', data.message || 'Unknown error');
        // API login failed
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      logError('💥 Login error:', error);
      logError('💥 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setError('Network error. Please try again.');
    } finally {
      log('🏁 Login process finished');
      setIsLoading(false);
      log('⏳ Loading state set to false');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    log('🔑 Forgot password form submitted');
    log('📧 Email:', email);
    
    if (!email) {
      log('❌ Email is empty');
      setForgotError('Please enter your email address.');
      return;
    }
    
    setForgotError('');
    setSuccessMsg(''); // Clear any previous success message
    
    try {
      const sendOtpUrl = getSendEmailOtpUrl();
      log('🌐 Making API call to send email OTP:', sendOtpUrl);
      log('🔗 Full API URL:', sendOtpUrl);
      log('🔗 Base URL:', 'https://api.jpnewz.com/dashboard');
      log('🔗 Endpoint:', '/send_email_forgot_password');
      
      const requestBody = {
        email: email
      };
      log('📦 Request body object:', requestBody);
      log('📦 Request body JSON string:', JSON.stringify(requestBody));
      log('📦 Request body type:', typeof JSON.stringify(requestBody));
      log('📦 Request body length:', JSON.stringify(requestBody).length);
      log('📦 Email being sent:', email);
      log('📦 Email validation:', {
        isString: typeof email === 'string',
        isEmpty: email === '',
        hasAtSymbol: email.includes('@'),
        length: email.length
      });

      log('📤 Starting fetch request for email OTP...');
      log('📤 Fetch details:', {
        url: sendOtpUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      const response = await fetch(sendOtpUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      log('📥 Fetch request completed');

      log('📡 Response status:', response.status);
      log('📡 Response headers:', response.headers);

      const data = await response.json();
      log('📄 Response data:', data);

      if (response.ok && data.status === true) {
        log('✅ Email OTP sent successfully!');
        log('📱 OTP data:', data);
        
        // Store the OTP from response if provided
        if (data.otp) {
          // setSentPin(data.otp); // This line is commented out
          log('📱 OTP from API:', data.otp);
        } else {
          // Fallback to a default OTP for testing
          // setSentPin('123456'); // This line is commented out
          log('📱 Using fallback OTP: 123456');
        }
        
        setForgotError('');
        setSuccessMsg('Email sent successfully! Please check your inbox for further instructions.');
        // setView('pin'); // Commented out - no longer redirecting to PIN verification
        log('🔄 Email sent successfully - staying on forgot password view');
      } else {
        log('❌ Email OTP failed:', data.message || 'Unknown error');
        log('❌ Error code:', data.error_code);
        log('❌ Full error response:', data);
        
        // Handle specific error cases
        if (data.error_code === 400 && data.message === 'Email already exists.') {
          setForgotError('This email is already registered. Please use a different email or try logging in.');
        } else {
          setForgotError(data.message || 'Failed to send OTP. Please try again.');
        }
      }
    } catch (error) {
      logError('💥 Email OTP error:', error);
      logError('💥 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setForgotError('Network error. Please try again.');
    }
  };

  // const handlePin = (e) => {
  //   e.preventDefault();
  //   log('🔢 PIN verification form submitted');
  //   log('📱 Entered PIN:', pin);
  //   log('📱 Expected PIN:', sentPin);
    
  //   if (pin === sentPin) {
  //     log('✅ PIN verification successful');
  //     // setPinError(''); // This line is commented out
  //     // setSuccessMsg('PIN verified! You can now reset your password.'); // This line is commented out
  //     // In a real app, show reset password form here
  //   } else {
  //     log('❌ PIN verification failed');
  //     // setPinError('Invalid PIN.'); // This line is commented out
  //   }
  // };

  log('🔄 Rendering Login component');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Left Side - Orange, 70% */}
      <Box
        sx={{
          flexBasis: { xs: '0%', md: '70%' },
          flexGrow: 1,
          background: 'linear-gradient(135deg, orange 80%, #ff9800 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          p: 4,
          minHeight: '100vh',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PublicIcon sx={{ fontSize: 160, mb: 3, color: 'white', opacity: 0.9 }} />
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ letterSpacing: 2 }}>
            JustPost
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85 }}>
            Welcome Back!<br />
            Manage your platform with ease.
          </Typography>
        </Box>
      </Box>
      {/* Right Side - Forms, 30% */}
      <Box
        sx={{
          flexBasis: { xs: '100%', md: '30%' },
          minWidth: { xs: '100%', md: 350 },
          maxWidth: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fafbfc',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 350, p: 4, position: 'relative', minHeight: 300 }}>
          <Fade in={view === 'login'} timeout={300} unmountOnExit>
            <Box sx={{ position: 'absolute', width: '100%', top: 0, left: 0, right: 0 }}>
              <Paper elevation={3} sx={{ p: 4 }}>
                <form onSubmit={handleLogin}>
                  <TextField
                    label="Username"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={username}
                    onChange={(e) => {
                      log('📝 Username changed:', e.target.value);
                      setUsername(e.target.value);
                    }}
                    required
                  />
                  <TextField
                    label="Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => {
                      log('📝 Password changed:', e.target.value ? '[HIDDEN]' : '[EMPTY]');
                      setPassword(e.target.value);
                    }}
                    required
                  />
                  {error && (
                    <Typography color="error" variant="body2" align="center" sx={{ mt: 1 }}>
                      {error}
                    </Typography>
                  )}
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    fullWidth 
                    sx={{ mt: 2 }} 
                    disabled={isLoading}
                    onClick={() => handleButtonClick('Login Submit')}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                  </Button>
                </form>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    variant="text" 
                    color="warning" 
                    onClick={() => {
                      handleButtonClick('Forgot Password');
                      setView('forgot');
                    }}
                  >
                    Forgot Password?
                  </Button>
                                     <Button 
                     variant="text" 
                     color="secondary" 
                     onClick={testConsole}
                     sx={{ mt: 1, mr: 1 }}
                   >
                     Test Console
                   </Button>
                   <Button 
                     variant="text" 
                     color="primary" 
                     onClick={testApiLogin}
                     sx={{ mt: 1, mr: 1 }}
                   >
                     Test API Login
                   </Button>
                   <Button 
                     variant="text" 
                     color="secondary" 
                     onClick={testEmailOtpApi}
                     sx={{ mt: 1 }}
                   >
                     Test Email OTP
                   </Button>
                </Box>
              </Paper>
            </Box>
          </Fade>
          <Fade in={view === 'forgot'} timeout={300} unmountOnExit>
            <Box sx={{ position: 'absolute', width: '100%', top: 0, left: 0, right: 0 }}>
              <Paper elevation={3} sx={{ p: 4 }}>
                <form onSubmit={handleForgot}>
                  <TextField
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => {
                      log('📝 Email changed:', e.target.value);
                      setEmail(e.target.value);
                    }}
                    required
                  />
                  {forgotError && (
                    <Typography color="error" variant="body2" align="center" sx={{ mt: 1 }}>
                      {forgotError}
                    </Typography>
                  )}
                  {successMsg && (
                    <Typography color="success.main" variant="body2" align="center" sx={{ mt: 1 }}>
                      {successMsg}
                    </Typography>
                  )}
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    fullWidth 
                    sx={{ mt: 2 }}
                    onClick={() => handleButtonClick('Send PIN')}
                  >
                    Send PIN
                  </Button>
                </form>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    variant="text" 
                    color="primary" 
                    onClick={() => {
                      handleButtonClick('Back to Login');
                      setView('login');
                      setSuccessMsg(''); // Clear success message when going back
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Fade>
          {/* <Fade in={view === 'pin'} timeout={300} unmountOnExit>
            <Box sx={{ position: 'absolute', width: '100%', top: 0, left: 0, right: 0 }}>
              <Paper elevation={3} sx={{ p: 4 }}>
                <form onSubmit={handlePin}>
                  <TextField
                    label="PIN"
                    type="text"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={pin}
                    onChange={(e) => {
                      log('📝 PIN changed:', e.target.value);
                      setPin(e.target.value);
                    }}
                    required
                  />
                  {pinError && (
                    <Typography color="error" variant="body2" align="center" sx={{ mt: 1 }}>
                      {pinError}
                    </Typography>
                  )}
                  {successMsg && (
                    <Typography color="success.main" variant="body2" align="center" sx={{ mt: 1 }}>
                      {successMsg}
                    </Typography>
                  )}
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    fullWidth 
                    sx={{ mt: 2 }}
                    onClick={() => handleButtonClick('Verify PIN')}
                  >
                    Verify PIN
                  </Button>
                </form>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    variant="text" 
                    color="primary" 
                    onClick={() => {
                      handleButtonClick('Back to Login from PIN');
                      setView('login');
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Fade> */}
        </Box>
      </Box>
    </Box>
  );
} 